import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SparkIcon } from '@spark';
import GenAiModalShell from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/GenAiModalShell';
import TextAreaField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextAreaField';
import {
    incompletePairedRowErrors,
    newPairedExampleRow,
    toWireContentGenerationExamples,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/PairedExampleRows';
import DefineTaskStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/DefineTaskStep';
import ReviewStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/ReviewStep';
import { createDefaultSettings } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import useDatasetNameField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/useDatasetNameField';
import {
    generationSessionConfig,
    pairedExampleRowsFromConfig,
    settingsFromConfig,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/generationSessionPrefill';
import useGenerationSessions from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationSessions';
import RecordCountInput from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/RecordCountInput';
import useGenerationTargets from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';
import {
    DEFAULT_TOTAL_RECORDS,
    EMPTY_CONTENT_GENERATION_DATASET_BANNER_TEXT,
    HELP_TEXTS,
    MIN_TOTAL_RECORDS,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { reportRequiredFieldsError } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/requiredFieldsError';
import { fetchGenerationDatasetNameSuggestion } from '@/features/datasets/utils/aiGenerationDataset';
import { retrievalTaskTypeLabel, sessionOptionTitle } from '@/features/datasets/utils/retrievalAgentDataset';

/** Define Task info banner: existing counts vs empty-dataset start message. */
export const formatContentGenerationBanner = (existingRecords) => {
    if (existingRecords > 0) {
        return (
            `You have ${existingRecords.toLocaleString('en-US')} records with outputs. ` +
            'Generate additional records to expand your dataset.'
        );
    }
    return EMPTY_CONTENT_GENERATION_DATASET_BANNER_TEXT;
};

/**
 * Content generation flow, parallel to the summarization flow: Define Task
 * (goal, description, Advanced settings whose Text Length sizes the generated
 * output) → Total Records + optional Input Text / Wanted Output example pairs
 * and optional Exclude → Review & Generate. Examples go out as
 * ``ContentGenerationExample`` rows.
 */
const ContentGenerationFlow = ({ open, onClose, onGenerate, usecase, warningBanner, disabled = false }) => {
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [goal, setGoal] = useState('');
    const [description, setDescription] = useState('');
    const [exampleRows, setExampleRows] = useState([]);
    const [exampleRowErrors, setExampleRowErrors] = useState({});
    const [exclude, setExclude] = useState('');
    const [errors, setErrors] = useState({});
    const [settings, setSettings] = useState(createDefaultSettings());
    const [totalRecords, setTotalRecords] = useState(DEFAULT_TOTAL_RECORDS);
    const [footerError, setFooterError] = useState('');
    const [loadedSessionTitle, setLoadedSessionTitle] = useState(undefined);

    const sessions = useGenerationSessions(usecase?._id, open);
    const { datasetName, onDatasetNameChange, datasetNameError, validateDatasetName } = useDatasetNameField({
        open,
        usecaseId: usecase?._id,
        fetchSuggestion: fetchGenerationDatasetNameSuggestion,
    });

    const { totalRecords: existingRecords } = useGenerationTargets(usecase, open);
    const bannerText = formatContentGenerationBanner(existingRecords);

    const totalRecordsError = !Number.isFinite(totalRecords) || totalRecords < MIN_TOTAL_RECORDS;

    const reset = () => {
        setStep(0);
        setGoal('');
        setDescription('');
        setExampleRows([]);
        setExampleRowErrors({});
        setExclude('');
        setErrors({});
        setSettings(createDefaultSettings());
        setTotalRecords(DEFAULT_TOTAL_RECORDS);
        setFooterError('');
        setLoadedSessionTitle(undefined);
    };

    /** Prefill the wizard from a past generation configuration (Dataset Name keeps its fresh default). */
    const applySession = (session) => {
        const config = generationSessionConfig(session);
        if (!config) return;
        setLoadedSessionTitle(sessionOptionTitle(session));
        setGoal(config.goal);
        setDescription(config.description);
        setErrors({});
        setSettings(settingsFromConfig(config));
        setExampleRows(pairedExampleRowsFromConfig(config));
        setExampleRowErrors({});
        setExclude(config.settings.exclude || '');
        if (Number.isFinite(config.datasetSize) && config.datasetSize > 0) {
            setTotalRecords(config.datasetSize);
        }
        setFooterError('');
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const clearError = (key) => setErrors((prev) => ({ ...prev, [key]: false }));

    const patchExampleRow = (id, key, value) => {
        setExampleRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
        setExampleRowErrors((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setFooterError('');
    };

    const validateStep0 = () => {
        const nextErrors = {
            goal: !goal.trim(),
            description: !description.trim(),
        };
        const datasetNameValid = validateDatasetName();
        setErrors(nextErrors);
        return !Object.values(nextErrors).some(Boolean) && datasetNameValid;
    };

    const handlePrimary = async () => {
        if (step === 0) {
            if (validateStep0()) {
                setFooterError('');
                setStep(1);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        if (step === 1) {
            // Examples and Exclude are optional, but a started example pair
            // must be completed before moving on.
            const nextRowErrors = incompletePairedRowErrors(exampleRows);
            setExampleRowErrors(nextRowErrors);
            if (!totalRecordsError && !Object.keys(nextRowErrors).length) {
                setFooterError('');
                setStep(2);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        try {
            setIsSubmitting(true);
            await onGenerate({
                datasetName: datasetName.trim(),
                goal: goal.trim(),
                description: description.trim(),
                totalRecords,
                examples: toWireContentGenerationExamples(exampleRows),
                exclude: exclude.trim(),
                settings,
            });
            handleClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GenAiModalShell
            open={open}
            onClose={handleClose}
            title="Generate data with AI"
            subtitle="Define what the dataset should teach, then configure the labels and examples that will guide generation."
            primaryLabel={step === 2 ? 'Generate' : 'Next'}
            onPrimary={handlePrimary}
            primaryDisabled={disabled}
            isSubmitting={isSubmitting}
            showBack={step > 0}
            onBack={() => {
                setFooterError('');
                setStep(step - 1);
            }}
            footerError={footerError}
        >
            {warningBanner}
            {step === 0 ? (
                <DefineTaskStep
                    bannerText={bannerText}
                    datasetName={datasetName}
                    onDatasetNameChange={onDatasetNameChange}
                    datasetNameError={datasetNameError}
                    sessions={sessions}
                    loadedSessionTitle={loadedSessionTitle}
                    onLoadSession={applySession}
                    goal={goal}
                    description={description}
                    goalError={!!errors.goal}
                    descriptionError={!!errors.description}
                    onGoalChange={(value) => {
                        setGoal(value);
                        clearError('goal');
                        setFooterError('');
                    }}
                    onDescriptionChange={(value) => {
                        setDescription(value);
                        clearError('description');
                        setFooterError('');
                    }}
                    settings={settings}
                    onSettingsChange={setSettings}
                />
            ) : null}
            {step === 1 ? (
                <div className="gdai-section">
                    <div className="gdai-records-row">
                        <div className="gdai-records-row__total">
                            <RecordCountInput
                                label="Total Records"
                                required
                                help={HELP_TEXTS.totalRecords}
                                value={totalRecords}
                                onChange={(value) => {
                                    setTotalRecords(value);
                                    setFooterError('');
                                }}
                                danger={totalRecordsError}
                            />
                        </div>
                    </div>
                    <div className="gdai-section-heading fs-unmask">Examples</div>
                    {exampleRows.map((row, index) => (
                        <div key={row.id} className="gdai-section">
                            <div className="gdai-label-header">
                                <span className="fs-unmask">{`Example (${index + 1})`}</span>
                                <button
                                    type="button"
                                    className="gdai-icon-button"
                                    aria-label={`Remove example ${index + 1}`}
                                    onClick={() =>
                                        setExampleRows((prev) => prev.filter((item) => item.id !== row.id))
                                    }
                                >
                                    <SparkIcon slug="trash" size="small" />
                                </button>
                            </div>
                            <div className="gdai-row">
                                <TextAreaField
                                    label="Input Text"
                                    placeholder="Original Text"
                                    value={row.text}
                                    onChange={(value) => patchExampleRow(row.id, 'text', value)}
                                    height={95}
                                    error={!!exampleRowErrors[row.id]?.text}
                                />
                                <TextAreaField
                                    label="Wanted Output"
                                    placeholder="Wanted output"
                                    value={row.summary}
                                    onChange={(value) => patchExampleRow(row.id, 'summary', value)}
                                    height={95}
                                    error={!!exampleRowErrors[row.id]?.summary}
                                />
                            </div>
                        </div>
                    ))}
                    <div>
                        <button
                            type="button"
                            className="gdai-tiny-button"
                            onClick={() => setExampleRows((prev) => [...prev, newPairedExampleRow()])}
                        >
                            <SparkIcon slug="plus" size="small" />
                            <span className="fs-unmask">Add Example</span>
                        </button>
                    </div>
                    <TextAreaField
                        label="Exclude"
                        help={HELP_TEXTS.exclude}
                        placeholder="e.g., Do not generate overly short or trivial inputs."
                        value={exclude}
                        onChange={(value) => {
                            setExclude(value);
                            setFooterError('');
                        }}
                        height={110}
                    />
                </div>
            ) : null}
            {step === 2 ? (
                <ReviewStep
                    goal={goal.trim()}
                    customGuidelines={description.trim()}
                    languages={settings.languages}
                    taskTypeLabel={retrievalTaskTypeLabel(usecase)}
                    totalRecords={totalRecords}
                />
            ) : null}
        </GenAiModalShell>
    );
};

ContentGenerationFlow.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    usecase: PropTypes.object,
    warningBanner: PropTypes.node,
    disabled: PropTypes.bool,
};

export default ContentGenerationFlow;
